/**
 * Locale-aware date formatting utilities.
 *
 * Use these for *display* — Spanish dates differ in word order ("6 de mayo
 * de 2026" vs "May 6, 2026") and time format (24h vs 12h). Keep moment.js
 * for arithmetic (elapsed time, comparisons) where locale doesn't matter.
 */
import type { TFunction } from "i18next";
import type { Moment } from "moment";

type DateLike = Date | Moment | number | string;

const LOCALE_BCP47: Record<string, string> = {
  en: "en-US",
  es: "es-ES",
};

export function toBcp47(locale: string): string {
  return LOCALE_BCP47[locale] ?? LOCALE_BCP47.en;
}

function toDate(input: DateLike): Date {
  if (input instanceof Date) return input;
  if (typeof input === "number") return new Date(input);
  if (typeof input === "string") return new Date(input);
  // moment.Moment has a toDate() method
  const maybeMoment = input as { toDate?: () => Date };
  if (typeof maybeMoment?.toDate === "function") {
    return maybeMoment.toDate();
  }
  return new Date(input as unknown as number);
}

export function formatLong(input: DateLike, locale: string): string {
  const date = toDate(input);
  if (isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(toBcp47(locale), {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

export function formatShort(input: DateLike, locale: string): string {
  const date = toDate(input);
  if (isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(toBcp47(locale), {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function formatTime(input: DateLike, locale: string): string {
  const date = toDate(input);
  if (isNaN(date.getTime())) return "";
  // Omit hour12 — let CLDR pick the locale-conventional clock for each locale
  // (en-US → 12h, es-ES → 24h, etc.). Hardcoding hour12 = (locale === "en")
  // would force any third locale we add into 24h regardless of convention.
  return new Intl.DateTimeFormat(toBcp47(locale), {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export function formatMonthYear(input: DateLike, locale: string): string {
  const date = toDate(input);
  if (isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(toBcp47(locale), {
    year: "numeric",
    month: "long",
  }).format(date);
}

export function formatDayMonth(input: DateLike, locale: string): string {
  const date = toDate(input);
  if (isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(toBcp47(locale), {
    day: "numeric",
    month: "long",
  }).format(date);
}

export function formatRelativeDay(
  input: DateLike,
  locale: string,
  t: TFunction,
  now: Date = new Date(),
): string {
  const date = toDate(input);
  if (isNaN(date.getTime())) return "";

  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round(
    (startOfDay(date) - startOfDay(now)) / (1000 * 60 * 60 * 24),
  );

  if (diffDays === 0) return t("common.today");
  if (diffDays === -1) return t("common.yesterday");

  return formatShort(date, locale);
}
